import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Settings,
  Users,
  Menu,
  X,
  Home,
  BarChart3,
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/utils/cn';

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  superAdminOnly?: boolean;
}

export function AdminSidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const collapsed = useAppStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems: NavItem[] = [
    { icon: LayoutDashboard, label: t('admin.dashboard.title'), path: '/admin' },
    { icon: Settings, label: t('admin.features.title'), path: '/admin/features' },
    { icon: Users, label: t('admin.users.title'), path: '/admin/users' },
    { icon: BarChart3, label: t('admin.pointsReport.title'), path: '/admin/points-report', superAdminOnly: true },
  ];

  const visibleNavItems = navItems.filter((item) => !item.superAdminOnly || user?.role === 'super_admin');

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    const isActive =
      location.pathname === item.path ||
      (item.path !== '/admin' && location.pathname.startsWith(`${item.path}/`));

    return (
      <div key={item.path} className="group relative px-2">
        <button
          onClick={() => handleNavigate(item.path)}
          className={cn(
            'w-full flex items-center gap-3 rounded-lg transition-colors duration-150',
            collapsed ? 'justify-center px-2 py-3' : 'px-3 py-2.5',
            isActive
              ? 'bg-surface-hover text-text-primary border-l-[3px] border-l-primary'
              : 'text-muted hover:bg-surface-hover hover:text-text-primary border-l-[3px] border-l-transparent'
          )}
        >
          <Icon
            size={20}
            className={cn(
              'shrink-0',
              isActive ? 'text-primary' : 'text-muted'
            )}
          />
          {!collapsed && (
            <span className="text-sm font-medium truncate">{item.label}</span>
          )}
        </button>

        {collapsed && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 bg-surface-hover text-text-primary text-xs rounded-md whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 border border-border z-50 shadow-lg pointer-events-none">
            {item.label}
            <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-1.5 h-1.5 bg-surface-hover rotate-45 border-l border-b border-border" />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-3 left-4 z-50 p-2 rounded-lg bg-surface border border-border text-text-secondary hover:text-text-primary transition-colors"
        aria-label={mobileOpen ? t('admin.sidebar.menuClose') : t('admin.sidebar.menuOpen')}
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-overlay/60 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          'flex flex-col bg-surface border-r border-border transition-all duration-200 ease-out',
          'fixed md:fixed inset-y-0 left-0 z-40 pt-14',
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          collapsed ? 'w-16' : 'w-36'
        )}
      >
        <nav className="flex-1 py-3 space-y-1 overflow-y-auto">
          {visibleNavItems.map(renderNavItem)}
        </nav>

        <div className="py-3 space-y-1 border-t border-border">
          <div className="group relative px-2">
            <button
              onClick={() => handleNavigate('/home')}
              className={cn(
                'w-full flex items-center gap-3 rounded-lg transition-colors duration-150',
                collapsed ? 'justify-center px-2 py-3' : 'px-3 py-2.5',
                'text-muted hover:bg-surface-hover hover:text-text-primary border-l-[3px] border-l-transparent'
              )}
            >
              <Home size={20} className="shrink-0 text-muted" />
              {!collapsed && (
                <span className="text-sm font-medium truncate">
                  {t('admin.backToHome')}
                </span>
              )}
            </button>

            {collapsed && (
              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 bg-surface-hover text-text-primary text-xs rounded-md whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 border border-border z-50 shadow-lg pointer-events-none">
                {t('admin.backToHome')}
                <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-1.5 h-1.5 bg-surface-hover rotate-45 border-l border-b border-border" />
              </div>
            )}
          </div>

          <div className="px-2">
            <button
              onClick={toggleSidebar}
              className={cn(
                'w-full flex items-center justify-center p-2 rounded-lg text-muted hover:bg-surface-hover hover:text-text-primary transition-colors',
                !collapsed && 'gap-2'
              )}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className={cn(
                  'transition-transform duration-200',
                  collapsed && 'rotate-180'
                )}
              >
                <path
                  d="M10 12L6 8L10 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {!collapsed && <span className="text-xs">{t('admin.sidebar.collapse')}</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
